import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { AdminUrlBuilder } from '../../../../../../shared/admin/api/admin-url-builder';
import { AbstractFormComponent } from '../../../../../../shared/crud/component/form.component';
import { IDropdownSearcherOptions } from '../../../../../../shared/ui/component/dropdown-searcher/dropdown-searcher.interfaces';
import { ProductOption, CampaignOption } from '../../types';
import { PrintCreditForm } from './print-credit-form.form';
import { CampaignStatus } from '../../../../../../shared/enum/campaign-status';

@Component({
    templateUrl: './print-credit-form.component.html',
    styleUrls: ['./print-credit-form.component.scss'],
})
export class AdminPrintCreditFormModal extends AbstractFormComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() campaignsOptions: any[];
    @Input() dropdownSearchOptions: IDropdownSearcherOptions;
    @Output() onPrintCreditSaved = new EventEmitter<any>();
    productOptions: ProductOption[] = [];

    private originalCampaignsOptionsSubject: BehaviorSubject<CampaignOption[]> = new BehaviorSubject<CampaignOption[]>([]);
    campaignsOptionsSubject: BehaviorSubject<CampaignOption[]> = new BehaviorSubject<CampaignOption[]>([]);
    campaignsOptions$: Observable<CampaignOption[]> = this.campaignsOptionsSubject.asObservable();
    campaignChange: Subscription;
    statuses: any = [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
    ];
    selectedCampaign: number = null;

    constructor(
        formBuilder: PrintCreditForm,
        httpClient: HttpClient,
        toastrService: ToastrService,
        urlBuilder: AdminUrlBuilder,
        router: Router,
        route: ActivatedRoute,
        public modalRef: BsModalRef
    ) {
        super(formBuilder, httpClient, toastrService, router, route, urlBuilder);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.formMode = this.record.id ? 'update' : 'create';
        if (this.record.id) {
            this.form.patchValue({
                status: this.record.status,
                product: this.record.product.id
            });
            this.onCampaignChange(this.record.campaign.id);
        }
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();

        if (!this.record.id) {
            this.originalCampaignsOptionsSubject.next([ ...this.campaignsOptions ]);
            this.campaignsOptionsSubject.next(this.originalCampaignsOptionsSubject.getValue());
        }
    }

    ngOnDestroy(): void {
        if (this.campaignChange) this.campaignChange.unsubscribe();
    }

    protected getUrl(id?: number): string {
        return `/print-credit${id ? '/' + id.toString() : ''}`;
    }

    protected onSubmit(response: any): void {
        this.toastrService.success('Saved');
        this.onPrintCreditSaved.emit(response);
        this.channel.next(this.record);
        this.record = {};
        this.form.reset();
    }

    onCampaignChange(campaignId: number): void {
        this.httpClient
            .get<any>(`${this.urlBuilder.buildUrl('/product')}`, { params: { campaign: String(campaignId), location: 'kiosk', limit: '999' } })
            .subscribe(
                (response: any) => {
                    this.productOptions = this.mapResponseToProductOptions(response, campaignId);
                },
                (error: any) => {
                    this.loading = false;
                }
            );
    }

    private parseLabel(language: string, itemName: string): string {
        try {
            const parsedLabel = JSON.parse(itemName);
            let label: string;
            if (parsedLabel[language]) {
                label = parsedLabel[language];
            } else if (parsedLabel.en) {
                label = parsedLabel.en;
            } else {
                label = parsedLabel[Object.keys(parsedLabel)[0]];
            }
            return label;
        } catch (e) {
            return itemName;
        }
    }

    private mapResponseToProductOptions(response: any, campaignId: number): ProductOption[] {
        const selectedCampaign = this.record.campaign ?
            this.record.campaign : this.campaignsOptions.find((campaign) => campaign.id === campaignId);

        return response.rows
            .map((item) => {
                const label: string = this.parseLabel(selectedCampaign.language, item.name);

                return {
                    label: label,
                    value: item.id,
                };
            });
    }

    handleCampaignSearch(phrase: string) {
        const originalOptions = this.originalCampaignsOptionsSubject.getValue();

        if (!phrase) {
            this.campaignsOptionsSubject.next(originalOptions);
            return;
        }
        const filteredOptions = originalOptions.filter((option) => {
            return option.label.toLowerCase().includes(phrase.toLowerCase()) || '';
        });

        this.campaignsOptionsSubject.next(filteredOptions);
    }

    handleCampaignChange(selectedItem: CampaignOption) {
        this.selectedCampaign = selectedItem.value;
        this.onCampaignChange(this.selectedCampaign);
    }

    submit(): void {
        this.formMode = 'create';
        if (this.form.invalid) {
            this.toastrService.error('Form contains incorrect data');
            return;
        }

        const form = {
            ...this.form.value,
            product: this.form.value.product ? +this.form.value.product : null,
        };

        const method = this.record.id ? 'put' : 'post';
        this.httpClient[method](`${this.urlBuilder.buildUrl(this.getUrl(this.record.id))}`, form).subscribe(
            (response: any) => {
                this.toastrService.success('Product added');
                this.modalRef.hide();
                this.onPrintCreditSaved.emit();
            },
            (error: any) => {
                this.toastrService.error(error.error ? error.error.message : 'Product has been not added');
            }
        );
    }
}
